import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import ItemQRCodeGenerator from '../orders/ItemQRCodeGenerator';
import CascadingSidePanel from './CascadingSidePanel';
import { closeItemSidePanel } from '../../store/slices/sidePanelSlice';
import { useDispatch } from 'react-redux';
import { printHTML } from "../../utils/printUtils";
import { generateQRLayout } from "../../utils/qrLayoutGenerator";
import { apiService } from "../../services/api";
import QRCode from 'qrcode';
import { enhancedApiClient, isSuccessResponse } from "../../services/enhancedApiClient";

interface Item {
  id: string;
  type: string;
  width?: string;
  height?: string;
  number?: string;
  status?: string;
  stages?: Array<{
    stage: string;
    status: string;
    timestamp?: string;
    completedBy?: string;
    workerName?: string;
    workerId?: string;
  }>;
}

interface Product {
  id: string;
  type: string;
  name?: string;
  frame?: Item[];
  panel?: Item[];
  panelWindow?: Item[];
  net?: Item[];
  openableType?: Item[];
}

interface ItemSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  product: Product | null;
  selectedItem: Item | null;
  onItemSelect: (item: Item) => void;
  onBackToItems?: () => void;
  onBackToProducts?: () => void;
  showStages?: boolean;
  title?: string;
  order?: any;
  selectedItemId?: string; // Track selected item
}

const ItemSidePanel: React.FC<ItemSidePanelProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  product,
  selectedItem,
  onItemSelect,
  onBackToItems,
  onBackToProducts,
  showStages = false,
  title,
  order,
  selectedItemId,
}) => {
  const dispatch = useDispatch();
  // QR Code modal state
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [selectedItemForQR, setSelectedItemForQR] = useState<any | null>(null);
  const [qrCodeData, setQrCodeData] = useState<any | null>(null);
  const [qrCodeImages, setQrCodeImages] = useState<{[key: string]: string}>({});
  const [loadingQR, setLoadingQR] = useState(false);
  const [subtypeLabelMap, setSubtypeLabelMap] = useState<Map<string, string>>(new Map());
  // Ref to track if we've already fetched subtypes for this product
  const fetchedSubtypesRef = useRef<string>('');

  useEffect(()=>{
    console.log('====>selectedItemForQR:',selectedItemForQR)
    console.log('====>selectedItem:',selectedItem)
    console.log('====>product:',product)
    console.log('====>showQRCodeModal:',showQRCodeModal)
    console.log('====>selectedItemForQR:',selectedItemForQR)
  },[selectedItemForQR,selectedItem,product,selectedItemForQR,showQRCodeModal])

  // Fetch QR code data when modal opens
  useEffect(() => {
    const fetchQRCodeData = async () => {
      if (!showQRCodeModal || !selectedItemForQR?.id) return;

      try {
        setLoadingQR(true);
        const result = await apiService.getItemQRCode(selectedItemForQR.id);
        
        if (result.success && result.data) {
          setQrCodeData(result.data);
          
          // Generate QR code image from qr_code string
          if (result.data.item?.qr_code) {
            const qrImage = await QRCode.toDataURL(result.data.item.qr_code, {
              width: 240,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              },
              errorCorrectionLevel: 'H'
            });
            setQrCodeImages({
              [selectedItemForQR.id]: qrImage
            });
          } else if (result.data.item?.qr_image_data) {
            // Use existing QR image data if available
            setQrCodeImages({
              [selectedItemForQR.id]: result.data.item.qr_image_data
            });
          }
        }
      } catch (err) {
        console.error('Error fetching QR code data:', err);
      } finally {
        setLoadingQR(false);
      }
    };

    fetchQRCodeData();
  }, [showQRCodeModal, selectedItemForQR?.id]);
  
  const capitalizeWords = (str: string) => {
    return str?.replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to check if an item is a panel with laser cutting enabled
  const hasLaserCutting = (item: any): boolean => {
    const isPanel = item.itemType === 'panel' || item.type === 'Panel';
    if (!isPanel) return false;
    
    const isLaserCutting = item.is_laser_cutting ?? item.isLaserCutting;
    return isLaserCutting === true || isLaserCutting === 'true' || isLaserCutting === 1;
  };

  // Helper function to check if an item is a panel with last cutting enabled
  const hasLastCutting = (item: any): boolean => {
    const isPanel = item.itemType === 'panel' || item.type === 'Panel';
    if (!isPanel) return false;
    
    const isLastCutting = item.is_last_cutting ?? item.isLastCutting ?? item.last_cutting ?? item.lastCutting;
    return isLastCutting === true || isLastCutting === 'true' || isLastCutting === 1;
  };

  // Fetch subtype definitions to build label map
  useEffect(() => {
    const fetchSubtypeLabels = async () => {
      if (!product || !isOpen) {
        // Reset ref when panel is closed
        if (!isOpen) {
          fetchedSubtypesRef.current = '';
        }
        return;
      }

      // Create a unique key for this product's item types
      const productType = product.type || '';
      const itemTypes = new Set<string>();

      // Collect all item types from the product
      const allItems = [
        ...(product.frame || []),
        ...(product.panel || []),
        ...(product.panelWindow || []),
        ...(product.net || []),
        ...(product.openableType || []),
      ];
      allItems.forEach((item: any) => {
        if (item.type) itemTypes.add(item.type);
      });

      // Create a unique key based on product ID, type, and item types
      const itemTypesKey = Array.from(itemTypes).sort().join(',');
      const fetchKey = `${product.id || ''}-${productType}-${itemTypesKey}`;

      // Skip if we've already fetched for this combination
      if (fetchedSubtypesRef.current === fetchKey) {
        return;
      }

      // Mark as fetched
      fetchedSubtypesRef.current = fetchKey;

      const labelMap = new Map<string, string>();

      // Fetch subtype definitions for all item types
      const fetchPromises: Promise<void>[] = [];
      itemTypes.forEach((itemType) => {
        const promise = enhancedApiClient
          .get(`/item-subtype-definitions/available?productType=${encodeURIComponent(productType)}&itemType=${encodeURIComponent(itemType)}`)
          .then((response) => {
            if (isSuccessResponse(response) && response.data) {
              const backendResponse = response.data as any;
              const config = backendResponse?.data || backendResponse;
              const subtypes = config?.subtypes || [];

              subtypes.forEach((subtypeGroup: any) => {
                const subtypeName = subtypeGroup.name || subtypeGroup.subtypeName;
                if (subtypeGroup.options && Array.isArray(subtypeGroup.options)) {
                  subtypeGroup.options.forEach((option: any) => {
                    const value = option.value || option.optionValue;
                    const label = option.label || option.optionLabel || value;
                    if (value) {
                      const key = `${productType}-${itemType}-${subtypeName}-${value}`;
                      labelMap.set(key, label);
                    }
                  });
                }
              });
            }
          })
          .catch((error) => {
            console.error(`Error fetching subtypes for ${productType}-${itemType}:`, error);
          });
        fetchPromises.push(promise);
      });

      await Promise.all(fetchPromises);
      setSubtypeLabelMap(labelMap);
    };

    fetchSubtypeLabels();
  }, [product?.id, product?.type, isOpen, product?.frame?.length, product?.panel?.length, product?.panelWindow?.length, product?.net?.length, product?.openableType?.length]);

  // Helper function to get label for a subtype value
  const getSubtypeLabel = (
    subtypeName: string,
    value: string,
    productType: string,
    itemType: string
  ): string => {
    const key = `${productType}-${itemType}-${subtypeName}-${value}`;
    return subtypeLabelMap.get(key) || value;
  };

  const handleViewQR = (item: any) => {
    console.log('View QR for item:', item);
    setSelectedItemForQR(item);
    setShowQRCodeModal(true);
  };

  // Debug logging
  // console.log('ItemSidePanel - Product data:', product);
  // console.log('ItemSidePanel - Product frame items:', product?.frame);
  // console.log('ItemSidePanel - Product panel items:', product?.panel);
  // console.log('ItemSidePanel - Product panelWindow items:', product?.panelWindow);
  // console.log('ItemSidePanel - Product net items:', product?.net);
  // console.log('ItemSidePanel - Product openableType items:', product?.openableType);

  // Get all items from the product
  const allItems = [
    ...(product?.frame || []).map(item => ({ ...item, itemType: 'frame' })),
    ...(product?.panel || []).map(item => ({ ...item, itemType: 'panel' })),
    ...(product?.panelWindow || []).map(item => ({ ...item, itemType: 'panelWindow' })),
    ...(product?.net || []).map(item => ({ ...item, itemType: 'net' })),
    ...(product?.openableType || []).map(item => ({ ...item, itemType: 'openableType' })),
  ];


  const handleCloseQRModal = () => {
    setShowQRCodeModal(false);
    setSelectedItemForQR(null);
  };

  const handlePrintSingle = async (item: any) => {
    console.log('Print QR for item:', item);
    
    try {
      // Fetch QR code data if not already available
      let itemQRData = qrCodeData;
      let itemQRImages = qrCodeImages;
      
      if (!itemQRData || itemQRData.item?.id !== item.id) {
        const result = await apiService.getItemQRCode(item.id);
        if (result.success && result.data) {
          itemQRData = result.data;
          
          // Generate QR code image
          if (result.data.item?.qr_code) {
            const qrImage = await QRCode.toDataURL(result.data.item.qr_code, {
              width: 240,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              },
              errorCorrectionLevel: 'H'
            });
            itemQRImages = {
              [item.id]: qrImage
            };
          } else if (result.data.item?.qr_image_data) {
            itemQRImages = {
              [item.id]: result.data.item.qr_image_data
            };
          }
        }
      }

      // Format item data similar to order QR codes format
      const formattedItem = {
        id: item.id,
        item_number: itemQRData?.item?.item_number || item.item_number || item.number || item.id,
        qr_code: itemQRData?.item?.qr_code || item.qr_code,
        type: itemQRData?.item?.type || item.type,
        current_stage: itemQRData?.item?.current_stage || item.current_stage,
        status: itemQRData?.item?.status || item.status,
        specifications: itemQRData?.item?.specifications || item.specifications || {},
        width: itemQRData?.item?.width || item.width,
        height: itemQRData?.item?.height || item.height,
        color: itemQRData?.item?.color || item.color,
        product: itemQRData?.item?.product || {
          id: product?.id,
          name: product?.name,
          type: product?.type,
          color: (product as any)?.color
        }
      };

      const html = generateQRLayout({
        singleItem: formattedItem,
        qrCodeImages: itemQRImages,
        qrData: itemQRData,
        order: itemQRData?.order || order,
      });

      printHTML({
        title: `QR - ${formattedItem.item_number}`,
        content: html,
        singlePage: true, // Single item QR code should print on one page
      });
    } catch (err) {
      console.error('Error printing QR code:', err);
    }
  };

  console.log('ItemSidePanel - All items:', allItems);

  return (
    <CascadingSidePanel
      isOpen={isOpen}
      onClose={onClose}
      onCloseAll={onCloseAll}
      panelId="item-panel"
      title={title || `${capitalizeWords(product?.type || '')} - Parts`}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Side Panel Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showStages && onBackToItems && (
                <IconButton 
                  onClick={onBackToItems}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  ←
                </IconButton>
              )}
              {!showStages && onBackToProducts && (
                <IconButton 
                  onClick={onBackToProducts}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  ←
                </IconButton>
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {capitalizeWords(title) || `${capitalizeWords(product?.type)} - Parts`}
              </Typography>
            </Box>
            <IconButton 
              onClick={() => {
                dispatch(closeItemSidePanel());
                onClose?.();
             }}
              size="small"
            >
              ✕
            </IconButton>
          </Box>

      {/* Side Panel Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {allItems.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '200px',
            textAlign: 'center'
          }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No parts found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This set doesn't have any parts yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {allItems.map((item, index) => {
              const isSelected = selectedItemId === item.id || selectedItem?.id === item.id;
              return (
              <Card 
                key={`${product?.id}-${(item as any).itemType}-${item.id}-${index}`} 
                sx={{ 
                  p: 1.5, 
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
                    transform: 'translateY(-1px)',
                    boxShadow: isSelected ? 3 : 1,
                    borderColor: 'primary.main'
                  },
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: isSelected ? 2 : 0,
                }}
                onClick={() => onItemSelect(item)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      bgcolor: item.itemType === 'frame' ? 'primary.main' : 'grey.400',
                      flexShrink: 0
                    }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      
                       { (item as any)?.number || item.id }
                       {hasLaserCutting(item) && (
                    <Chip
                      label="LC"
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        fontWeight: 600,
                        color: '#d32f2f', // Red text
                        backgroundColor: '#ffebee', // Light red background
                        border: '1px solid #ef9a9a'
                      }}
                    />
                  )}
                      
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={capitalizeWords(item.status === 'in_progress' ? 'In Progress' :  item.status) || 'pending'}
                      color={item.status === 'completed' ? 'success' : 
                             item.status === 'in_progress' ? 'primary' : 'warning'}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<QrCodeIcon sx={{ fontSize: '0.8rem' }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewQR(item);
                        // handlePrintSingle(allItems)
                      }}
                      sx={{
                        minWidth: 'auto',
                        px: 1,
                        py: 0.25,
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'primary.light'
                        }
                      }}
                    >
                      QR
                    </Button>                               
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {item.type?.charAt(0).toUpperCase() + item.type?.slice(1) || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                    
                    {`${parseInt(item.width)}w × ${item.height ? parseInt(item.height) + "h" : "-"}`}

                  </Typography>
                
                </Box>
                
                {/* Subtype display */}
                {(() => {
                  const subtype = (item as any).subtype;
                  const productType = product?.type || '';
                  const itemType = item.type || '';
                  
                  if (subtype && typeof subtype === 'object' && !Array.isArray(subtype)) {
                    const subtypeLabels: string[] = [];
                    Object.keys(subtype).forEach((key) => {
                      const values = subtype[key];
                      if (Array.isArray(values) && values.length > 0) {
                        const labels = values.map((val: string) => 
                          getSubtypeLabel(key, val, productType, itemType)
                        );
                        // Add each label separately (without subtype name)
                        subtypeLabels.push(...labels);
                      } else if (values) {
                        const label = getSubtypeLabel(key, values, productType, itemType);
                        subtypeLabels.push(label);
                      }
                    });
                    if (subtypeLabels.length > 0) {
                      return (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: '#1976d2', // Blue highlight color
                              backgroundColor: '#e3f2fd', // Light blue background
                              padding: '2px 8px',
                              borderRadius: '4px',
                              display: 'inline-block',
                              border: '1px solid #90caf9'
                            }}
                          >
                            {subtypeLabels.join(' • ')}
                          </Typography>
                        </Box>
                      );
                    }
                  }
                  return null;
                })()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Stages Progress
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                      {(() => {
                        const stages = item.stages || [];
                        const completed = stages.filter(s => s.status === 'completed').length;
                        const total = stages.length;
                        return `${completed}/${total}`;
                      })()}
                    </Typography>
                    {/* Show last completed stage worker */}
                    {(() => {
                      const stages = item.stages || [];
                      const lastCompletedStage = stages
                        .filter(s => s.status === 'completed')
                        .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())[0];
                      
                      if (lastCompletedStage && (lastCompletedStage.workerName || lastCompletedStage.completedBy || (lastCompletedStage as any).assigned_to || (lastCompletedStage as any).worker)) {
                        return (
                          <Typography variant="caption" color="success.main" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                            Last: {lastCompletedStage.workerName || lastCompletedStage.completedBy || (lastCompletedStage as any).assigned_to || (lastCompletedStage as any).worker}
                          </Typography>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Next Stage
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                      {(() => {
                        const stages = item.stages || [];
                        const nextStage = stages.find(s => s.status !== 'completed');
                        return nextStage ? nextStage.stage : 'Complete';
                      })()}
                    </Typography>
                  </Box>
                </Box>

              </Card>
            );
            })}
          </Box>
        )}
      </Box>

      {/* Item QR Code Modal */}
      {showQRCodeModal && selectedItemForQR && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={handleCloseQRModal}
        >
          <Box
            sx={{
              // bgcolor: 'background.paper',
              // borderRadius: 3,
              p: 4,
              // maxWidth: 700,
              width: '40%',
              height: '100%',
              // overflow: 'auto',
              overflowY: 'scroll', // or 'auto'
              scrollbarWidth: 'none', // Firefox
              '&::-webkit-scrollbar': {
                display: 'none', // Chrome, Safari
              },
              scrollbarColor: 'rgba(0,0,0,0.2) transparent',
              // boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
              // border: '1px solid',
              // borderColor: 'divider'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QrCodeIcon sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Item QR Code
                </Typography>
              </Box>
              <IconButton 
                onClick={handleCloseQRModal} 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    color: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                ✕
              </IconButton>
            </Box> */}
            
            {product ? (
                <ItemQRCodeGenerator
                  itemId={selectedItemForQR.id}
                  orderId={qrCodeData?.order?.id || order?.id || ''}
                  productId={product.id || ''}
                  itemType={selectedItemForQR.type || selectedItemForQR.itemType || 'item'}
                  itemName={(selectedItemForQR as any)?.number || selectedItemForQR.id}
                  showQRCode={true}
                  existingQrCode={qrCodeData?.item?.qr_code || selectedItemForQR.qr_code}
                  existingQrImageUrl={qrCodeImages[selectedItemForQR.id] || qrCodeData?.item?.qr_image_data || selectedItemForQR.qr_code_image_url}
                  productName={qrCodeData?.item?.product?.name || product?.name || product?.type}
                  customerName={qrCodeData?.order?.customer_name || order?.customer?.name}
                  width={qrCodeData?.item?.width || selectedItemForQR.width}
                  height={qrCodeData?.item?.height || selectedItemForQR.height}
                  color={qrCodeData?.item?.color || selectedItemForQR.color}
                  qrCodeData={qrCodeData}
                  qrCodeImages={qrCodeImages}
                  onQRCodeGenerated={(qrCode) => {
                    console.log('QR Code generated for item:', selectedItemForQR.id, qrCode);
                    // You can add additional logic here if needed
                  }}
                  onClose={handleCloseQRModal}
                />
              // </Box>
            ) : (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                bgcolor: 'error.light',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'error.main'
              }}>
                <Typography variant="h6" color="error.main" gutterBottom>
                  Error: Product information not available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Please close this modal and try again. Make sure you're viewing the item from the correct product.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Product information is required to generate QR codes.
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseQRModal}
                  sx={{ 
                    mt: 2,
                    borderColor: 'error.main',
                    color: 'error.main',
                    '&:hover': {
                      borderColor: 'error.dark',
                      backgroundColor: 'error.light'
                    }
                  }}
                >
                  Close
                </Button>
              </Box>
            )}
          </Box> 
        </Box> 
      )}

      </Box>
    </CascadingSidePanel>
  );
};

export default ItemSidePanel;
